/**
 * In-drawer preview for files on a Monday item (Dean, 20 Oct 2026: "we need to be able to preview (eye icon) these
 * files, in monday mirror. same for au"). Fetches through the portal's signed-in session (no 10-minute token, no new
 * tab the browser can block), shows PDFs / images / HTML inline and says plainly why when it can't.
 */
import { useEffect, useState } from "react";
import { Loader2, Download, ExternalLink, AlertTriangle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import apiClient from "@/lib/api";

const kindOf = (name, type) => {
  const n = (name || "").toLowerCase();
  if (type.startsWith("image/") || /\.(png|jpe?g|gif|webp|bmp|heic)$/.test(n)) return "image";
  if (type.includes("pdf") || n.endsWith(".pdf")) return "pdf";
  if (type.startsWith("text/html") || /\.html?$/.test(n)) return "html";
  if (type.startsWith("text/") || /\.(txt|csv|log|md)$/.test(n)) return "text";
  return "other";
};

export function MirrorFilePreview({ file, onClose }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let url = null;
    let dead = false;
    (async () => {
      try {
        const r = await apiClient.get(file.endpoint || "/applications/mirror/files/view", {
          params: file.endpoint ? (file.params || {}) : { src: file.url, name: file.name || "file" }, responseType: "blob", timeout: 90000,
        });
        if (dead) return;
        const type = r.headers?.["content-type"] || r.data?.type || "";
        url = URL.createObjectURL(r.data);
        setState({ loading: false, url, type, kind: kindOf(file.name, type) });
      } catch (e) {
        if (dead) return;
        let detail = e?.message || "Preview failed";
        try {
          if (e?.response?.data instanceof Blob) detail = JSON.parse(await e.response.data.text())?.detail || detail;
          else detail = e?.response?.data?.detail || detail;
        } catch { /* keep message */ }
        setState({ loading: false, error: detail });
      }
    })();
    return () => { dead = true; if (url) URL.revokeObjectURL(url); };
    // keyed on the file's identity, not the object — parents that poll re-create `file` each render (Dean, 28 Oct 2026: signed agreement kept reloading)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file.endpoint, file.url, file.name, JSON.stringify(file.params || null)]);

  const download = () => {
    if (!state.url) return;
    const a = document.createElement("a");
    a.href = state.url; a.download = file.name || "file"; a.click();
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl w-[96vw] h-[90vh] flex flex-col p-0 gap-0" aria-describedby={undefined} data-testid="mirror-file-preview">
        <DialogHeader className="px-4 py-2.5 border-b flex-row items-center gap-3 space-y-0">
          <DialogTitle className="text-sm flex items-center gap-2 min-w-0 flex-1">
            <FileText className="w-4 h-4 text-slate-500 shrink-0" />
            <span className="truncate" data-testid="mirror-file-preview-name">{file.name}</span>
            {file.size ? <span className="text-[10px] text-slate-400 shrink-0">{Math.round(file.size / 1024)} KB</span> : null}
          </DialogTitle>
          <div className="flex items-center gap-1.5 mr-6">
            <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={!state.url} onClick={download} data-testid="mirror-file-preview-download">
              <Download className="w-3 h-3 mr-1" /> Download
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={!state.url}
              onClick={() => window.open(state.url, "_blank", "noopener")} data-testid="mirror-file-preview-newtab">
              <ExternalLink className="w-3 h-3 mr-1" /> New tab
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 min-h-0 bg-slate-100 flex items-center justify-center">
          {state.loading && (
            <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="mirror-file-preview-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Fetching from {file.source || "Monday"}…
            </div>
          )}
          {state.error && (
            <div className="max-w-md text-center text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-4 py-3 flex items-start gap-2" data-testid="mirror-file-preview-error">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> <span>{state.error}</span>
            </div>
          )}
          {state.url && state.kind === "image" && (
            <img src={state.url} alt={file.name} className="max-w-full max-h-full object-contain" data-testid="mirror-file-preview-image" />
          )}
          {state.url && (state.kind === "pdf" || state.kind === "text") && (
            <iframe title={file.name} src={state.url} className="w-full h-full bg-white" data-testid="mirror-file-preview-frame" />
          )}
          {state.url && state.kind === "html" && (
            <iframe title={file.name} src={state.url} sandbox="" className="w-full h-full bg-white" data-testid="mirror-file-preview-frame" />
          )}
          {state.url && state.kind === "other" && (
            <div className="text-center text-xs text-slate-600 space-y-2" data-testid="mirror-file-preview-other">
              <div>No inline preview for this file type ({state.type || "unknown"}).</div>
              <Button size="sm" onClick={download}><Download className="w-3.5 h-3.5 mr-1" /> Download {file.name}</Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
